/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, warp-drive/no-legacy-request-patterns */
// Extends from
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
// Ember imports
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { setDiff } from '@ember/object/computed';
// Internal imports
import {
  destroyAndSaveRelated,
  loadRelated,
} from 'linced-admin/utils/destroy-record';

export default class IndexController extends Controller {
  // Services
  @service
  paperToaster;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  toaster;

  // Attributes
  @tracked
  categoryId = '';

  @tracked
  label = '';

  @action
  setLabel(value) {
    this.label = value;
  }

  @tracked
  errorMessage = '';

  @tracked
  categoryDialogOpen = false;

  currentStep = 0;

  @tracked
  saveInProgress = false;

  // Applying template attributes
  applyConfirmed = false;

  applyDialogOpen = false;
  applyErrors = null;
  applyInProgress = false;
  selectedTemplatesToApply = [];
  selectedSchoolsToApply = [];
  selectedGroupFilter = null;

  // Usage sets
  @tracked
  usageSetDialogOpen = false;

  @tracked
  currentCategory = null;

  @tracked
  selectedUsageSets = [];

  @tracked
  initialUsageSets = [];

  @setDiff('model.usageSets', 'selectedUsageSetsFilter')
  remainingUsageSetsFilter;

  @tracked
  selectedUsageSetsFilter = [];

  @tracked
  deleteCategoryDialogOpen;

  @tracked
  deleteInProgress;

  @computed(
    'model.tagCategories.@each.usageSetIds',
    'selectedUsageSetsFilter.[]',
  )
  get filteredCategories() {
    let selectedUsageSets = this.selectedUsageSetsFilter;
    return this.get('model.tagCategories').filter(function (tagCategory) {
      if (!isEmpty(selectedUsageSets)) {
        return selectedUsageSets.some((usageSet) =>
          tagCategory.get('usageSetsArray').includes(usageSet),
        );
      }
      return true;
    });
  }

  // toggleDialog opens the category dialog
  toggleDialog() {
    this.errorMessage = '';
    this.toggleProperty('categoryDialogOpen');
    if (!this.categoryDialogOpen) {
      this.resetForm();
    }
  }

  // recordsReferencing returns the usage sets and page templates that point
  // at a tag category. They get saved after it is deleted so the server drops
  // the stale id; destroyAndSaveRelated explains why they are gathered first.
  async recordsReferencing(tagCategory) {
    const [usageSets, pageTemplates] = await Promise.all([
      loadRelated(tagCategory, ['usageSetIds']),
      this.store.query('posts/page', {
        tagCategoryIds: JSON.stringify([tagCategory.id]),
      }),
    ]);
    return [...usageSets, ...pageTemplates.slice()];
  }

  @action
  toggleCategoryDialog() {
    this.toggleDialog();
  }

  @action
  editCategory(id) {
    this.store.findRecord('posts/tag-category', id).then((category) => {
      this.label = category.get('label');
    });
    this.categoryId = id;
    this.toggleDialog();
  }

  @action
  saveCategory() {
    this.saveInProgress = true;
    this.errorMessage = '';
    let promise;
    let action = 'create';

    if (isEmpty(this.categoryId)) {
      promise = new Promise((resolve) => {
        let record = this.store.createRecord('posts/tag-category', {
          label: this.label,
        });
        resolve(record);
      });
    } else {
      action = 'update';
      promise = new Promise((resolve) => {
        const id = this.categoryId;
        this.store.findRecord('posts/tag-category', id).then((category) => {
          category.set('label', this.label);

          resolve(category);
        });
      });
    }

    promise.then((record) => {
      return record
        .save()
        .then(() => {
          if (action === 'create') {
            // Send an event to trigger the route to refresh the model
            this.router.refresh('tags.index');
          }

          this.categoryDialogOpen = false;
          this.resetForm();
        })
        .catch((err) => {
          this.handleError(err, action);
          record.rollbackAttributes();
        })
        .then(() => {
          this.saveInProgress = false;
        });
    });
  }

  @action
  deleteCategory(id) {
    this.errorMessage = '';
    this.store.findRecord('posts/tag-category', id).then((category) => {
      this.label = category.get('label');
    });
    this.categoryId = id;
    this.deleteCategoryDialogOpen = true;
  }

  @action
  async confirmDelete(confirmed) {
    if (!confirmed) {
      // Tidy up / close dialog.
      this.deleteCategoryDialogOpen = false;
      this.errorMessage = '';
      this.resetForm();
      return;
    }

    // Deletion confirmed; actually delete the record.
    this.deleteInProgress = true;
    this.errorMessage = '';

    // Grab record from local store - no GET request, as that causes weirdness.
    // We only need the ID to issue the DELETE anyway, which can't change, so no need to use findRecord.
    let category = this.store.peekRecord('posts/tag-category', this.categoryId);
    try {
      let related = await this.recordsReferencing(category);

      // Delete record (locally + pushed to server), then save the records
      // that referenced it.
      let { deleted, error } = await destroyAndSaveRelated(category, related);
      if (!deleted) {
        this.handleError(error, 'delete');
        return;
      }
      if (error) {
        this.toaster.error(
          `The tag category was deleted, but removing it from its usage sets and page templates failed.`,
        );
      } else {
        this.toaster.success(`The tag category has been deleted successfully.`);
      }

      // Tidy up / close dialog.
      this.deleteCategoryDialogOpen = false;
      this.resetForm();
    } catch (err) {
      // Finding the referencing records failed; nothing has been deleted.
      this.handleError(err, 'delete');
    } finally {
      this.deleteInProgress = false;
    }
  }

  @action
  openUsageSetDialog(category) {
    category
      .get('usageSetIds')
      .then((usageSetsArray) => {
        this.usageSetDialogOpen = true;
        this.currentCategory = category;
        this.selectedUsageSets = usageSetsArray.slice();
        this.initialUsageSets = usageSetsArray.slice();
      })
      .catch(() => {
        this.toaster.error(
          `Error trying to edit the tag category's usage sets`,
        );
      });
  }

  @action
  toggleUsageSet(usageSet, checked) {
    if (checked) {
      this.selectedUsageSets = [...this.selectedUsageSets, usageSet];
    } else {
      this.selectedUsageSets = this.selectedUsageSets.filter(
        (u) => u !== usageSet,
      );
    }
  }

  @action
  saveCategoryAndUsageSet() {
    this.saveInProgress = true;
    let promises = [];
    let changedUsageSets = [];

    // Update the usage sets
    let removedUsageSets = this.initialUsageSets.filter(
      (usageSet) => !this.selectedUsageSets.includes(usageSet),
    );
    let addedUsageSets = this.selectedUsageSets.filter(
      (usageSet) => !this.initialUsageSets.includes(usageSet),
    );
    changedUsageSets = changedUsageSets.concat(
      removedUsageSets,
      addedUsageSets,
    );
    if (!isEmpty(changedUsageSets)) {
      // Usage sets a page has been newly added to/removed from
      changedUsageSets.forEach((changedUsageSet) => {
        promises.push(changedUsageSet.save());
      });
    }

    // Save the page template at the end
    this.currentCategory.set('usageSetIds', this.selectedUsageSets);
    promises.push(this.currentCategory.save());

    return Promise.all(promises)
      .then(() => {
        // After saving the page and all involved usage sets
        this.toaster.success(
          `Successfully saved the tag category and usage sets`,
        );
        this.currentCategory.reload();
        this.usageSetDialogOpen = false;
        this.currentCategory = null;
        this.selectedUsageSets = [];
        this.initialUsageSets = [];
        this.saveInProgress = false;
      })
      .catch(() => {
        this.toaster.error(`Error saving the tag category's usage sets`);
        // Put the usage sets back, which also reverts each usage set's side.
        // rollbackAttributes() does not touch relationships.
        this.currentCategory.set('usageSetIds', this.initialUsageSets);
        this.currentCategory.rollbackAttributes();
        this.saveInProgress = false;
      });
  }

  @action
  addUsageSetFilter(usageSet) {
    this.selectedUsageSetsFilter = [...this.selectedUsageSetsFilter, usageSet];
  }

  @action
  removeUsageSetFilter(usageSet) {
    this.selectedUsageSetsFilter = this.selectedUsageSetsFilter.filter(
      (u) => u !== usageSet,
    );
  }

  handleError(err) {
    if (!isEmpty(err?.errors?.msg)) {
      this.errorMessage = err.errors.msg;
    } else {
      this.errorMessage = err?.message;
    }
  }

  resetForm() {
    this.categoryId = '';
    this.label = '';
  }
}
